import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';
import {
    TActionDraft,
    TActionExecution,
    TAssistantMessage,
} from 'src/modules/assistant/types/message';
import { RECOMMENDATION_FEEDBACK } from '../constants';
import { getRequestErrorKey } from '../helpers/getRequestErrorKey';

interface UseActionRecordingParams {
    scopeKey: string;
    contextLeadId: string | null;
    canUseAssistant: boolean;
    isCurrent: (requestScope: string) => boolean;
    setMessages: Dispatch<SetStateAction<TAssistantMessage[]>>;
    setError: Dispatch<SetStateAction<string>>;
}

const isForRequest = (entry: TAssistantMessage, requestId: string) =>
    entry.requestId === requestId ||
    entry.recommendation?.requestId === requestId;

/**
 * Records executed actions and feedback on assistant suggestions. Responses that
 * arrive after the user switched chats don't touch the new chat's messages.
 */
export const useActionRecording = ({
    scopeKey,
    contextLeadId,
    canUseAssistant,
    isCurrent,
    setMessages,
    setError,
}: UseActionRecordingParams) => {
    const { t } = useTranslation();

    const updateRequestMessages = useCallback(
        (requestId: string, changes: Partial<TAssistantMessage>) =>
            setMessages((previous) =>
                previous.map((entry) =>
                    isForRequest(entry, requestId)
                        ? { ...entry, ...changes }
                        : entry
                )
            ),
        [setMessages]
    );

    const completeAssistantAction = useCallback(
        async (
            requestId: string,
            action: TActionDraft
        ): Promise<TActionExecution | null> => {
            if (!contextLeadId || !requestId || !canUseAssistant) return null;
            const requestScope = scopeKey;
            try {
                const response = await api.post(
                    `/ai/leads/${contextLeadId}/chat/actions/${requestId}/complete`,
                    {
                        type: action.type,
                        scheduledAt: action.scheduledAt || null,
                    },
                    HEADERS()
                );
                const execution = response.data.data as TActionExecution;
                if (isCurrent(requestScope))
                    updateRequestMessages(requestId, {
                        actionExecution: execution,
                    });
                return execution;
            } catch (requestError) {
                if (isCurrent(requestScope))
                    setError(
                        t(
                            getRequestErrorKey(
                                requestError,
                                'Assistant.ActionSaveError'
                            )
                        )
                    );
                return null;
            }
        },
        [
            canUseAssistant,
            contextLeadId,
            isCurrent,
            scopeKey,
            t,
            updateRequestMessages,
        ]
    );

    const rateActionSuggestion = useCallback(
        async (requestId: string) => {
            if (!contextLeadId || !requestId || !canUseAssistant) return false;
            const requestScope = scopeKey;
            try {
                await api.post(
                    `/ai/leads/${contextLeadId}/chat/actions/${requestId}/feedback`,
                    { feedback: RECOMMENDATION_FEEDBACK.USEFUL },
                    HEADERS()
                );
                if (isCurrent(requestScope))
                    updateRequestMessages(requestId, {
                        feedback: RECOMMENDATION_FEEDBACK.USEFUL,
                    });
                return true;
            } catch (_) {
                if (isCurrent(requestScope))
                    setError(t('Assistant.ErrorFeedback'));
                return false;
            }
        },
        [
            canUseAssistant,
            contextLeadId,
            isCurrent,
            scopeKey,
            t,
            updateRequestMessages,
        ]
    );

    return useMemo(
        () => ({ completeAssistantAction, rateActionSuggestion }),
        [completeAssistantAction, rateActionSuggestion]
    );
};
